'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { Treatment } from '@/lib/treatments-data';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Star, CheckCircle, Calendar } from 'lucide-react';
import Breadcrumbs from '@/components/Breadcrumbs';
import TreatmentGallery from './treatment-gallery';
import ProcedureSteps from './procedure-steps';
import BookingForm from './booking-form';
import Footer from '@/components/layout/footer';
import SectionTitle from '@/components/home/section-title';

interface TreatmentDetailsProps {
  treatment: Treatment;
}

export default function TreatmentDetails({ treatment }: TreatmentDetailsProps) {
  const beforeAfterImages = [
    {
      before: 'https://images.pexels.com/photos/3845675/pexels-photo-3845675.jpeg',
      after: 'https://images.pexels.com/photos/3845676/pexels-photo-3845676.jpeg',
      caption: 'Complete smile transformation',
    },
    {
      before: 'https://images.pexels.com/photos/3845677/pexels-photo-3845677.jpeg',
      after: 'https://images.pexels.com/photos/3845678/pexels-photo-3845678.jpeg',
      caption: 'Natural-looking results',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      country: 'United States',
      rating: 5,
      text: 'The results exceeded my expectations. The entire process was comfortable and professional.',
      image: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg',
    },
    {
      name: 'Michael Schmidt',
      country: 'Germany',
      rating: 5,
      text: 'Outstanding results and exceptional care throughout the treatment.',
      image: 'https://images.pexels.com/photos/614810/pexels-photo-614810.jpeg',
    },
  ];

  const faqs = [
    {
      question: 'How long does the procedure take?',
      answer: treatment.treatmentTime,
    },
    {
      question: 'What is the success rate?',
      answer: treatment.successRate,
    },
    {
      question: 'Is the procedure painful?',
      answer: 'We use advanced techniques and proper anesthesia to ensure your comfort throughout the procedure.',
    },
    {
      question: 'How long is the recovery period?',
      answer: 'Recovery time varies by patient but typically ranges from a few days to a couple of weeks.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative h-[60vh] bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="absolute inset-0">
          <Image
            src={treatment.heroImage}
            alt={treatment.title}
            fill={true}
            style={{ objectFit: "cover" }}
            className="opacity-20"
            priority
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/90" />
        <div className="container relative h-full flex flex-col justify-center items-center text-white text-center z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 font-display"
          >
            {treatment.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-white/90 max-w-2xl"
          >
            {treatment.description}
          </motion.p>
        </div>
      </div>

      <div className="container py-8">
        <Breadcrumbs />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-16">

            {/* Overview Section */}
            <section>
              <SectionTitle>Treatment Overview</SectionTitle>

              {/* Quick Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Card>
                  <CardContent className="p-6 text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-primary" />
                    <h3 className="font-semibold font-display">Duration</h3>
                    <p>{treatment.treatmentTime}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <Star className="w-8 h-8 mx-auto mb-2 text-primary" />
                    <h3 className="font-semibold font-display">Success Rate</h3>
                    <p>{treatment.successRate}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <Calendar className="w-8 h-8 mx-auto mb-2 text-primary" />
                    <h3 className="font-semibold font-display">Recovery</h3>
                    <p>1-2 weeks</p>
                  </CardContent>
                </Card>
              </div>

              {/* Description and Benefits */}
              <div className="prose max-w-none bg-muted/30 rounded-lg p-8">
                <h2 className="font-display text-2xl mb-4">About {treatment.title}</h2>
                <p className="text-lg text-muted-foreground mb-6">{treatment.description}</p>

                <h3 className="font-display text-xl mb-4">Key Benefits</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>Natural-looking results that last</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>Minimally invasive procedure</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>Improved oral function and aesthetics</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>Boost in confidence and quality of life</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Procedure Section */}
            <section>
              <SectionTitle>Procedure Steps</SectionTitle>
              <ProcedureSteps
                steps={[
                  {
                    title: 'Initial Consultation',
                    description: 'Comprehensive examination and treatment planning',
                    duration: '30-45 minutes',
                    image: treatment.heroImage,
                  },
                  {
                    title: 'Preparation',
                    description: 'Preparing the treatment area and ensuring your comfort',
                    duration: '15-30 minutes',
                    image: treatment.heroImage,
                  },
                  {
                    title: 'Main Procedure',
                    description: 'Performing the treatment with precision and care',
                    duration: treatment.treatmentTime,
                    image: treatment.heroImage,
                  },
                  {
                    title: 'Recovery & Aftercare',
                    description: 'Post-treatment care instructions and follow-up plan',
                    duration: '30 minutes',
                    image: treatment.heroImage,
                  },
                ]}
              />
            </section>

            {/* Gallery Section */}
            <section>
              <SectionTitle>Before & After Gallery</SectionTitle>
              <TreatmentGallery images={beforeAfterImages} />
            </section>

            {/* Patient Reviews Section */}
            <section>
              <SectionTitle>Patient Reviews</SectionTitle>
              <div className="space-y-6">
                {testimonials.map((testimonial, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="relative w-16 h-16 rounded-full overflow-hidden">
                          <Image
                            src={testimonial.image}
                            alt={testimonial.name}
                            fill={true}
                            style={{ objectFit: "cover" }}
                          />
                        </div>
                        <div>
                          <h4 className="font-semibold">{testimonial.name}</h4>
                          <p className="text-sm text-muted-foreground">{testimonial.country}</p>
                          <div className="flex gap-1 my-2">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star
                                key={i}
                                className="w-4 h-4 fill-yellow-400 text-yellow-400"
                              />
                            ))}
                          </div>
                          <p className="text-muted-foreground">{testimonial.text}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* FAQ Section */}
            <section>
              <SectionTitle>Frequently Asked Questions</SectionTitle>
              <div className="bg-muted/30 rounded-lg p-8">
                <div className="space-y-6">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border-b border-border pb-4 last:border-b-0 last:pb-0">
                      <h4 className="font-medium mb-2 font-display text-lg">{faq.question}</h4>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4">Book Your Consultation</h3>
                  <BookingForm treatmentType={treatment.title} />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4">Why Choose Us</h3>
                  <ul className="space-y-4">
                    {[
                      'State-of-the-art facility',
                      'Experienced specialists',
                      'Personalized care',
                      'Latest technology',
                      'Comfortable environment',
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-primary" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
